import {
  calculatemainProductCurrentSupplierIncGstPrice,
  calculateMainProductJaybelIncGstPrice,
  calculateAlternateProductJaybelIncGSTPrice,
} from './calculatePrices';

/**
 * Normalizes quoteProducts array in a single pass with early exit.
 * Automatically populates missing GST Inclusive prices and totals for main and alternate products.
 * 
 * @param {Array} quoteProducts - Array of quote product objects
 * @returns {Array} Normalized quoteProducts array
 */
export const normalizeQuoteProducts = (quoteProducts) => {
  if (!Array.isArray(quoteProducts) || quoteProducts.length === 0) {
    return quoteProducts;
  }

  // Early Exit Check: Check if any product is actually missing inclusive prices or totals
  const needsNormalization = quoteProducts.some((product) => {
    const mainSupplierInc = product.mainProductCurrentSupplierIncGstPrice;
    const mainSupplierExc = product.mainProductCurrentSupplierExcGstPrice;
    const mainJaybelInc = product.mainProductJaybelIncGstPrice;
    const mainJaybelExc = product.mainProductJaybelExcGstPrice;
    const mainSupplierIncTotal = product.mainProductCurrentSupplierIncGstPriceTotal;
    const mainJaybelIncTotal = product.mainProductJaybelIncGstPriceTotal;

    const mainNeedsCalc =
      ((mainSupplierInc === null || mainSupplierInc === undefined || mainSupplierInc === '') &&
        mainSupplierExc !== null && mainSupplierExc !== undefined && mainSupplierExc !== '') ||
      ((mainJaybelInc === null || mainJaybelInc === undefined || mainJaybelInc === '') &&
        mainJaybelExc !== null && mainJaybelExc !== undefined && mainJaybelExc !== '') ||
      mainSupplierIncTotal === null || mainSupplierIncTotal === undefined || mainSupplierIncTotal === '' ||
      mainJaybelIncTotal === null || mainJaybelIncTotal === undefined || mainJaybelIncTotal === '';

    if (mainNeedsCalc) return true;

    if (product.alternateProduct) {
      const altSupplierInc = product.alternateProductCurrentSupplierIncGstPrice;
      const altSupplierExc = product.mainProductCurrentSupplierExcGstPrice;
      const altJaybelInc = product.alternateProductJaybelIncGstPrice;
      const altJaybelExc = product.alternateProductJaybelExcGstPrice;
      const altSupplierIncTotal = product.alternateProductCurrentSupplierIncGstPriceTotal;
      const altJaybelIncTotal = product.alternateProductJaybelIncGstPriceTotal;

      return (
        ((altSupplierInc === null || altSupplierInc === undefined || altSupplierInc === '') &&
          altSupplierExc !== null && altSupplierExc !== undefined && altSupplierExc !== '') ||
        ((altJaybelInc === null || altJaybelInc === undefined || altJaybelInc === '') &&
          altJaybelExc !== null && altJaybelExc !== undefined && altJaybelExc !== '') ||
        altSupplierIncTotal === null || altSupplierIncTotal === undefined || altSupplierIncTotal === '' ||
        altJaybelIncTotal === null || altJaybelIncTotal === undefined || altJaybelIncTotal === ''
      );
    }

    return false;
  });

  // If no products need normalization, exit immediately without looping or copying
  if (!needsNormalization) {
    return quoteProducts;
  }

  // Single-pass normalization for items requiring calculation
  return quoteProducts.map((product) => {
    const mainTaxCode = product.mainProduct?.taxCode;
    const quantity = Number(product.mainProductQuantity) || 0;

    let mainSupplierExc = product.mainProductCurrentSupplierExcGstPrice;
    let mainSupplierInc = product.mainProductCurrentSupplierIncGstPrice;
    let mainJaybelExc = product.mainProductJaybelExcGstPrice;
    let mainJaybelInc = product.mainProductJaybelIncGstPrice;

    // Current Supplier Price Inc Calculation
    if (mainSupplierInc === null || mainSupplierInc === undefined || mainSupplierInc === '') {
      if (mainSupplierExc !== null && mainSupplierExc !== undefined && mainSupplierExc !== '') {
        const excNum = Number(mainSupplierExc);
        if (!isNaN(excNum)) {
          mainSupplierInc =
            mainTaxCode === 'E'
              ? excNum
              : calculatemainProductCurrentSupplierIncGstPrice(excNum);
        }
      }
    }

    // Jaybel Price Inc Calculation
    if (mainJaybelInc === null || mainJaybelInc === undefined || mainJaybelInc === '') {
      if (mainJaybelExc !== null && mainJaybelExc !== undefined && mainJaybelExc !== '') {
        const excNum = Number(mainJaybelExc);
        if (!isNaN(excNum)) {
          mainJaybelInc =
            mainTaxCode === 'E'
              ? excNum
              : calculateMainProductJaybelIncGstPrice(excNum);
        }
      }
    }

    // Main Product Totals
    const mainSupplierIncTotal =
      mainSupplierInc !== null && mainSupplierInc !== undefined && mainSupplierInc !== '' && quantity > 0
        ? parseFloat((Number(mainSupplierInc) * quantity).toFixed(2))
        : product.mainProductCurrentSupplierIncGstPriceTotal;

    const mainJaybelIncTotal =
      mainJaybelInc !== null && mainJaybelInc !== undefined && mainJaybelInc !== '' && quantity > 0
        ? parseFloat((Number(mainJaybelInc) * quantity).toFixed(2))
        : product.mainProductJaybelIncGstPriceTotal;

    const normalizedProduct = {
      ...product,
      mainProductCurrentSupplierIncGstPrice: mainSupplierInc ?? '',
      mainProductJaybelIncGstPrice: mainJaybelInc ?? '',
      mainProductCurrentSupplierIncGstPriceTotal: mainSupplierIncTotal,
      mainProductJaybelIncGstPriceTotal: mainJaybelIncTotal,
    };

    // Alternate Product Normalization (if present)
    if (product.alternateProduct) {
      const altTaxCode = product.alternateProduct?.taxCode;
      const altQuantity = Number(product.alternateProductQuantity) || 0;
      const unitDiviser = Number(product.alternateProductUnitDiviser) || 1;

      let altSupplierExc = mainSupplierExc !== null && mainSupplierExc !== undefined && mainSupplierExc !== ''
        ? Number(mainSupplierExc) * unitDiviser
        : product.alternateProductCurrentSupplierExcGstPrice;
      let altSupplierInc = product.alternateProductCurrentSupplierIncGstPrice;
      let altJaybelExc = product.alternateProductJaybelExcGstPrice;
      let altJaybelInc = product.alternateProductJaybelIncGstPrice;

      if (altSupplierInc === null || altSupplierInc === undefined || altSupplierInc === '') {
        if (altSupplierExc !== null && altSupplierExc !== undefined && altSupplierExc !== '') {
          const excNum = Number(altSupplierExc);
          if (!isNaN(excNum)) {
            altSupplierInc =
              altTaxCode === 'E'
                ? excNum
                : parseFloat((excNum * 1.1).toFixed(2));
          }
        }
      }

      if (altJaybelInc === null || altJaybelInc === undefined || altJaybelInc === '') {
        if (altJaybelExc !== null && altJaybelExc !== undefined && altJaybelExc !== '') {
          const excNum = Number(altJaybelExc);
          if (!isNaN(excNum)) {
            altJaybelInc =
              altTaxCode === 'E'
                ? excNum
                : calculateAlternateProductJaybelIncGSTPrice(excNum);
          }
        }
      }

      const altSupplierIncTotal =
        altSupplierInc !== null && altSupplierInc !== undefined && altSupplierInc !== '' && altQuantity > 0
          ? parseFloat((Number(altSupplierInc) * altQuantity).toFixed(2))
          : product.alternateProductCurrentSupplierIncGstPriceTotal;

      const altJaybelIncTotal =
        altJaybelInc !== null && altJaybelInc !== undefined && altJaybelInc !== '' && altQuantity > 0
          ? parseFloat((Number(altJaybelInc) * altQuantity).toFixed(2))
          : product.alternateProductJaybelIncGstPriceTotal;

      normalizedProduct.alternateProductCurrentSupplierIncGstPrice = altSupplierInc ?? '';
      normalizedProduct.alternateProductJaybelIncGstPrice = altJaybelInc ?? '';
      normalizedProduct.alternateProductCurrentSupplierIncGstPriceTotal = altSupplierIncTotal;
      normalizedProduct.alternateProductJaybelIncGstPriceTotal = altJaybelIncTotal;
    }

    return normalizedProduct;
  });
};
